import ReactDOM from 'react-dom/client';
import { ReactNotifications } from 'react-notifications-component';
import { Provider } from 'react-redux';
import { BrowserRouter, Navigate, useRoutes, useParams } from 'react-router-dom';

import { AttendanceRoutesConfig } from '@/Modules/AttendanceModule/shared/config/attendance-routes.config';
import { AuthRoutesConfig } from '@/Modules/AuthModule/shared/config/auth-routes.config';
import { CatalogueRoutesConfig } from '@/Modules/CatalogueModule/shared/config/catalogue-routes.config';
import { CommunicationRoutesConfig } from '@/Modules/CommunicationModule/shared/config/communication-routes.config';
import {
  CustomersRoutesConfig,
  CustomerAnalyticsRouteConfig
} from '@/Modules/CustomersModule/shared/config/customers-routes.config';
import { DashboardRoutesConfig } from '@/Modules/DashboardModule/shared/config/dashboard-routes.config';
import { FinanceApplicationsRoutesConfig } from '@/Modules/FinanceApplicationsModule/shared/config/finance-applications-routes.config';
import { HrRoutesConfig } from '@/Modules/HRModule/shared/config/hr-routes.config';
import { LeadsRoutesConfig } from '@/Modules/LeadsModule/shared/config/leads-routes.config';
import { MerchantRoutesConfig } from '@/Modules/MerchatsModule/shared/config/merchant-routes.config';
import { ProductsRoutesConfig } from '@/Modules/ProductsModule/AutoProductsModule/shared/config/products-routes.config';
import { FinancialProductsRoutesConfig } from '@/Modules/ProductsModule/FinancialProductsModule/shared/config/financial-products-routes.config';
import { PurchasesRoutesConfig } from '@/Modules/PurchaseOrdersModule/shared/config/purchase-routes.config';
import { ReferralsRoutesConfig } from '@/Modules/referralsModule/shared/config/referrals-routes.config';
import { AiUsageReportRoutesConfig } from '@/Modules/ReportsModule/AiUsageReport/shared/config/ai-usage-report-routes.config';
import { FinanceApplicationsReportsRoutesConfig } from '@/Modules/ReportsModule/FinanceApplicationReport/shared/config/finance-applications-reports-routes.config';
import { PurchaseOrdersReportsRoutesConfig } from '@/Modules/ReportsModule/PurchaseOrdersReport/shared/config/purchase-orders-reports-routes.config';
import { SalesManagementRoutesConfig } from '@/Modules/SalesManagementModule/shared/config/sales-management-routes.config';
import { ServiceCenterRequestsRoutesConfig } from '@/Modules/ServiceCenterRequestsModule/shared/config/service-center-requests-routes.config';
import { BrandsRoutesConfig } from '@/Modules/SettingsModule/brands/shared/config/brands-routes.config';
import { CitiesAreasRoutesConfig } from '@/Modules/SettingsModule/cities-areas/shared/config/cities-areas-routes.config';
import { TransactionsRoutesConfig } from '@/Modules/TransactionsModule/shared/config/transaction-routes.config';
import { UsersManagementRoutesConfig } from '@/Modules/UserManagement/shared/config/user-management-routes.config';
import { WalletsRoutesConfig } from '@/Modules/WalletsModule/shared/config/wallets-routes.config';
import { initializeI18n } from '@/shared/config/i18n.config';
import { PUBLIC_PATH, APP_ROUTES } from '@/shared/config/routes-config';
import { AuthStatus } from '@/shared/enums/auth.status.enum';
import { RouteConfig } from '@/shared/interfaces/routes-config.interface';
import { PushNotificationProvider } from '@/shared/providers/push-notification/push-notification.provider';
import QueryClientProvider from '@/shared/providers/QueryClientProvider';
import ReduxStore from '@/shared/redux/store';
import { refreshUserSession } from '@/shared/services/data/user-data/auth.data';
import { initializeNotifications } from '@/shared/services/firebase/notifications.service';
import { initializeTrackingTools } from '@/shared/services/utils/tracking/tracking.service';
import CustomTheme from '@/shared/themes';
import { setupGlobalErrorHandlers } from '@/shared/utils/monitoring/error-tracker';
import { initializeOTel } from '@/shared/utils/monitoring/otel-browser';

import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import 'react-notifications-component/dist/theme.css';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './index.scss';
import { CashInsRoutesConfig } from './Modules/CashInsModule/shared/config/cashins-routes.config';
import { CashoutRoutesConfig } from './Modules/CashoutModule/shared/config/cashout-routes.config';
import { ServiceCenterProductsRoutesConfig } from './Modules/ProductsModule/serviceCenterProductsModule/shared/config/service-center-products-routes.config';

import { HelpdeskRoutesConfig } from '@/Modules/HelpdeskModule/shared/config/helpdesk-routes.config';
// import app modules

const store = ReduxStore;
initializeTrackingTools();
initializeI18n();
initializeNotifications(); // Initialize Firebase notifications

// Initialize OpenTelemetry monitoring
initializeOTel();
setupGlobalErrorHandlers();
const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

refreshUserSession().then((response) => {
  if (
    response === AuthStatus.UNAUTHORIZED &&
    window.location.href.indexOf(
      (APP_ROUTES.AUTH_MODULE.CHILDREN as RouteConfig).UNAUTHORIZED.FULL_PATH
    ) < 0
  ) {
    console.log('User not premitted to access admin page');
    window.location.href =
      PUBLIC_PATH + (APP_ROUTES.AUTH_MODULE.CHILDREN as RouteConfig).UNAUTHORIZED.FULL_PATH;
    // setTimeout(() => {
    //   history.push((APP_ROUTES.AUTH_MODULE.CHILDREN as RouteConfig).UNAUTHORIZED.FULL_PATH);
    // }, 500);
  }
});

// Component to redirect old notification URLs to new format
const ApplicationRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/finance-applications/details/${id}`} replace />;
};

const App = () => {
  console.log('Configure app routes');
  const routes = useRoutes([
    {
      index: true,
      element: <Navigate to={APP_ROUTES.AUTH_MODULE.FULL_PATH} />
    },
    {
      path: APP_ROUTES.AUTH_MODULE.FULL_PATH,
      children: AuthRoutesConfig
    },
    // URL rewrite for notification compatibility
    {
      path: '/applications/:id',
      element: <ApplicationRedirect />
    },
    {
      path: APP_ROUTES.DASHBOARD_MODULE.FULL_PATH,
      children: DashboardRoutesConfig
    },
    {
      path: APP_ROUTES.CATALOGUE_MODULE.FULL_PATH,
      children: CatalogueRoutesConfig
    },
    {
      path: APP_ROUTES.COMMUNICATION_MODULE.FULL_PATH,
      children: CommunicationRoutesConfig
    },
    {
      path: APP_ROUTES.MERCHANT_MODULE.FULL_PATH,
      children: MerchantRoutesConfig
    },
    {
      path: APP_ROUTES.PRODUCTS_MODULE.FULL_PATH,
      children: ProductsRoutesConfig
    },
    {
      path: APP_ROUTES.FINANCIAL_PRODUCTS_MODULE.FULL_PATH,
      children: FinancialProductsRoutesConfig
    },
    {
      path: APP_ROUTES.CUSTOMERS_MODULE.FULL_PATH,
      children: CustomersRoutesConfig
    },
    {
      path: APP_ROUTES.CUSTOMER_ANALYTICS_MODULE.FULL_PATH,
      children: CustomerAnalyticsRouteConfig
    },
    {
      path: APP_ROUTES.FINANCE_APPLICATIONS_MODULE.FULL_PATH,
      children: FinanceApplicationsRoutesConfig
    },
    {
      path: APP_ROUTES.LEADS_MODULE.FULL_PATH,
      children: LeadsRoutesConfig
    },
    {
      path: APP_ROUTES.TRANSACTIONS_MODULE.FULL_PATH,
      children: TransactionsRoutesConfig
    },
    {
      path: APP_ROUTES.PURCHASES_MODULE.FULL_PATH,
      children: PurchasesRoutesConfig
    },
    {
      path: APP_ROUTES.REPORTS_MODULE.FULL_PATH,
      children: FinanceApplicationsReportsRoutesConfig
    },
    {
      path: APP_ROUTES.PURCHASE_ORDERS_REPORT_MODULE.FULL_PATH,
      children: PurchaseOrdersReportsRoutesConfig
    },
    {
      path: APP_ROUTES.AI_USAGE_REPORT_MODULE.FULL_PATH,
      children: AiUsageReportRoutesConfig
    },
    {
      path: APP_ROUTES.USER_MANAGEMENT_MODULE.FULL_PATH,
      children: UsersManagementRoutesConfig
    },
    {
      path: APP_ROUTES.SALES_MANAGEMENT_MODULE.FULL_PATH,
      children: SalesManagementRoutesConfig
    },
    {
      path: APP_ROUTES.BRANDS_MODULE.FULL_PATH,
      children: BrandsRoutesConfig
    },
    {
      path: APP_ROUTES.CITIES_AREAS_MODULE.FULL_PATH,
      children: CitiesAreasRoutesConfig
    },
    {
      path: APP_ROUTES.REFERRALS_MODULE.FULL_PATH,
      children: ReferralsRoutesConfig
    },
    {
      path: APP_ROUTES.SERVICE_CENTER_PRODUCTS_MODULE.FULL_PATH,
      children: ServiceCenterProductsRoutesConfig
    },
    {
      path: APP_ROUTES.WALLETS_MODULE.FULL_PATH,
      children: WalletsRoutesConfig
    },
    {
      path: APP_ROUTES.CASH_OUT_MODULE.FULL_PATH,
      children: CashoutRoutesConfig
    },
    {
      path: APP_ROUTES.CASH_IN_MODULE.FULL_PATH,
      children: CashInsRoutesConfig
    },
    {
      path: APP_ROUTES.ATTENDANCE_MODULE.FULL_PATH,
      children: AttendanceRoutesConfig
    },
    {
      path: APP_ROUTES.SERVICE_CENTER_REQUESTS_MODULE.FULL_PATH,
      children: ServiceCenterRequestsRoutesConfig
    },
    {
      path: APP_ROUTES.HR_MODULE.FULL_PATH,
      children: HrRoutesConfig
    },
    {
      path: APP_ROUTES.HELPDESK_MODULE.FULL_PATH,
      children: HelpdeskRoutesConfig
    }
  ]);
  return routes;
};

root.render(
  <>
    <ReactNotifications />
    <QueryClientProvider>
      <Provider store={store}>
        <BrowserRouter basename={PUBLIC_PATH || '/'}>
          <CustomTheme>
            <PushNotificationProvider>
              <App />
            </PushNotificationProvider>
          </CustomTheme>
        </BrowserRouter>
      </Provider>
    </QueryClientProvider>
  </>
);
